import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';

import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { attendanceConceptService } from '@/api/domains/attendance';
import type { AttendanceConceptItem } from '@/api/domains/attendance';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useAppSelector } from '@/hooks';


type AttendanceConceptFilters = Record<string, never>;

const EMPTY_CELL = '-';

const renderCell = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return EMPTY_CELL;
  const text = String(value).trim();
  return text === '' ? EMPTY_CELL : text;
};

export interface UseAttendanceConceptListResult {
  rows: AttendanceConceptItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<AttendanceConceptItem>[];
  actions: TableAction<AttendanceConceptItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  refetch: () => void;
  hasRexIntegration: boolean;
  isSyncing: boolean;
  searchText: string;
  onCreate: () => void;
  onSync: () => void;
}

export function useAttendanceConceptList(): UseAttendanceConceptListResult {
  const { showApiError, showSuccess, openSidePanel } = useUI();
  const { t: translate } = useTranslation();
  const { canView, canEdit } = usePagePermissions();
  const hasRexIntegration = useAppSelector(
    (state) => state.auth.user?.syncAssistance ?? false,
  );
  const [isSyncing, setIsSyncing] = useState(false);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<AttendanceConceptFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<AttendanceConceptItem> | null> => {
      const response = await attendanceConceptService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          sort: params.sort
            ? {
              key: params.sort.key,
              direction: params.sort.direction.toLowerCase() as 'asc' | 'desc',
            }
            : undefined,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:attendanceConcept.messages.loadError'));
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    debouncedSearchText,
    refetch,
  } = usePaginatedList<AttendanceConceptItem, AttendanceConceptFilters>({
    fetchFn,
    initialFilters: {},
    initialSort: { key: 'name', direction: 'ASC' },
    screenCode: ROUTE_DEFINITIONS.CONCEPTOS_ASISTENCIA.code,
  });

  const columns: TableColumn<AttendanceConceptItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('attendance:attendanceConcept.fields.id'),
        sortable: true,
        render: (item) => renderCell(item.id),
      },
      {
        key: 'name',
        header: translate('attendance:attendanceConcept.fields.name'),
        sortable: true,
        render: (item) => renderCell(item.name),
      },
      {
        key: 'type',
        header: translate('attendance:attendanceConcept.fields.type'),
        sortable: true,
        render: (item) => renderCell(item.type),
      },
      {
        key: 'code',
        header: translate('attendance:attendanceConcept.fields.code'),
        sortable: true,
        render: (item) => renderCell(item.code),
      },
      {
        key: 'calculationFormula',
        header: translate('attendance:attendanceConcept.fields.calculationFormula'),
        sortable: true,
        render: (item) => renderCell(item.calculationFormula),
      },
      {
        key: 'obtentionFormula',
        header: translate('attendance:attendanceConcept.fields.obtentionFormula'),
        sortable: true,
        render: (item) => renderCell(item.obtentionFormula),
      },
      {
        key: 'handling',
        header: translate('attendance:attendanceConcept.fields.handling'),
        sortable: true,
        render: (item) => renderCell(item.handling),
      },
    ],
    [translate],
  );

  const handleViewDetail = useCallback(
    (row: AttendanceConceptItem) => {
      openSidePanel({
        title: translate('attendance:attendanceConcept.detail.title'),
        component: 'ConceptDetailPanel',
        props: { conceptId: row.id, readOnly: true },
      });
    },
    [openSidePanel, translate],
  );

  const handleEdit = useCallback(
    (row: AttendanceConceptItem) => {
      openSidePanel({
        title: translate('attendance:attendanceConcept.edit.title'),
        component: 'ConceptDetailPanel',
        props: { conceptId: row.id, onSaved: refetch },
      });
    },
    [openSidePanel, translate, refetch],
  );

  const actions: TableAction<AttendanceConceptItem>[] = useMemo(
    () => [
      {
        id: 'view',
        label: translate('common:tableActions.detail'),
        icon: 'eye',
        hidden: () => !canView,
        onClick: handleViewDetail,
      },
      {
        id: 'edit',
        label: translate('common:tableActions.edit'),
        icon: 'edit',
        hidden: () => !canEdit,
        onClick: handleEdit,
      },
    ],
    [translate, canView, canEdit, handleViewDetail, handleEdit],
  );

  const onCreate = useCallback(() => {
    openSidePanel({
      title: translate('attendance:attendanceConcept.create.title'),
      component: 'ConceptCreatePanel',
      props: { onSaved: refetch },
    });
  }, [openSidePanel, translate, refetch]);
  const onSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const response = await attendanceConceptService.sync();
      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:attendanceConcept.messages.syncError'));
        return;
      }
      showSuccess(response.data.summary);
      refetch();
    } finally {
      setIsSyncing(false);
    }
  }, [showApiError, showSuccess, translate, refetch]);
  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    refetch,
    hasRexIntegration,
    isSyncing,
    searchText: debouncedSearchText,
    onCreate,
    onSync,
  };
}

export default useAttendanceConceptList;
